import React, { useState, useEffect } from 'react';
import { getAppointmentsByClinicApi } from '../../../shared/api/appointmentApi';
import type { SecretaryUser } from '../../../shared/api/secretaryApi';

interface AppointmentsProps {
  user: SecretaryUser;
}

interface Appointment {
  appointmentId: string;
  fullName: string;
  phoneNumber: string;
  village: string;
  provider: string;
  serviceType: number;
  serviceName: string;
  selectedDay: string;
  selectedDayLabel: string;
  selectedTime: string;
  status: string;
  createdAt: string;
  createdBy: string;
  clinicName: string;
}

const Appointments: React.FC<AppointmentsProps> = ({ user }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAppointments();
  }, [user.clinicName]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading appointments for clinic:', user.clinicName);
      const data = await getAppointmentsByClinicApi(user.clinicName);
      console.log('Received appointments:', data);
      console.log('Appointments count:', data.length);
      console.log('Clinic names in appointments:', data.map(a => a.clinicName));
      setAppointments(data);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('שגיאה בטעינת התורים');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800 border-green-300',
      draft: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
    };
    
    const statusLabels = {
      completed: 'מאושר',
      draft: 'טיוטה',
      cancelled: 'מבוטל',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${statusStyles[status as keyof typeof statusStyles] || 'bg-gray-100 text-gray-800 border-gray-300'}`}>
        {statusLabels[status as keyof typeof statusLabels] || status}
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('he-IL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Page Title */}
        <div className="text-center mb-4 sm:mb-8 animate-fade-in">
          <h2 className="text-2xl sm:text-4xl font-bold text-slate-800 mb-2">התורים שלי</h2>
          <p className="text-sm sm:text-base text-slate-600">רשימת התורים שנקבעו ע"י המרפאה</p>
          <div className="mt-4 inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-teal-50 border border-teal-200 rounded-lg">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="text-xs sm:text-sm font-semibold text-teal-800">מציג תורים עבור: {user.clinicName}</span>
          </div>
        </div>

        {/* Appointments List */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-3 sm:p-8 border border-slate-100">
          {loading ? (
            <div className="text-center py-16">
              <div className="relative inline-block">
                <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
              </div>
              <p className="mt-6 text-lg font-medium text-slate-700">טוען תורים...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 bg-red-50 border-2 border-red-200 rounded-2xl">
              <svg className="w-16 h-16 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-bold text-red-800">{error}</p>
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-12 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl">
              <svg className="w-16 h-16 mx-auto text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-lg font-bold text-amber-800 mb-2">אין תורים להצגה</p>
              <p className="text-sm text-amber-700">לא נמצאו תורים עתידיים למרפאה זו</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View - Hidden on Mobile */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-teal-50 to-blue-50 border-b-2 border-slate-200">
                      <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">תאריך ושעה</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">שם מטופל</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">טלפון</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">יישוב</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">שירות</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">סטטוס</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-slate-800">נוצר ב</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.map((appointment, index) => (
                      <tr
                        key={appointment.appointmentId}
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <div>
                              <div className="font-bold text-slate-800">{formatDate(appointment.selectedDay)}</div>
                              <div className="text-sm text-slate-600" dir="ltr">{appointment.selectedTime}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-slate-800">{appointment.fullName}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-slate-700" dir="ltr">{appointment.phoneNumber}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-slate-700">{appointment.village}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="font-semibold text-slate-800">{appointment.serviceName || 'צילום רנטגן'}</div>
                            <div className="text-xs text-slate-500">{appointment.serviceType === 1 ? 'Xray' : 'US'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(appointment.status)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-600">
                            {formatDate(appointment.createdAt)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View - Visible only on Mobile */}
              <div className="md:hidden space-y-4">
                {appointments.map((appointment) => (
                  <div key={appointment.appointmentId} className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-2xl p-4 shadow-md hover:shadow-lg transition-shadow">
                    {/* Header: Date and Status */}
                    <div className="flex items-start justify-between mb-3 pb-3 border-b border-slate-200">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <div className="font-bold text-slate-800 text-sm">{formatDate(appointment.selectedDay)}</div>
                          <div className="text-xs text-slate-600" dir="ltr">{appointment.selectedTime}</div>
                        </div>
                      </div>
                      {getStatusBadge(appointment.status)}
                    </div>

                    {/* Patient Info */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="font-semibold text-slate-800">{appointment.fullName}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-slate-700 text-sm" dir="ltr">{appointment.phoneNumber}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-slate-700 text-sm">{appointment.village}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <div>
                          <span className="text-slate-800 text-sm font-medium">{appointment.serviceName || 'צילום רנטגן'}</span>
                          <span className="text-xs text-slate-500 mr-2">({appointment.serviceType === 1 ? 'Xray' : 'US'})</span>
                        </div>
                      </div>

                      <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">
                        נוצר ב: {formatDate(appointment.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Appointments;
